import { api } from '../api/vykoClient';
import { PatientHandoverItem, HandoverTaskStatus } from '../types';
import { appLogger } from '../utils/logger';
import { userService } from '../services/userService';

export class BatchAcceptModal {
  private container: HTMLElement;
  private onComplete: () => void;
  private tasks: HandoverTaskStatus[] = [];
  private isProcessing = false;
  private shouldAbort = false;
  private completedCount = 0;
  private successCount = 0;
  private failedCount = 0;

  constructor(container: HTMLElement, patients: PatientHandoverItem[], onComplete: () => void) {
    this.container = container;
    this.onComplete = onComplete;

    this.tasks = patients.map((p, idx) => ({
      id: `task-${p.hospitalNumber}-${p.ipNumber}-${idx}`,
      patientName: p.patientName || 'Unnamed Patient',
      hospitalNumber: String(p.hospitalNumber),
      ipNumber: String(p.ipNumber),
      status: 'pending',
    }));

    this.renderInitialUI();
    this.startHighSpeedBatchAcceptance();
  }

  private renderInitialUI() {
    const total = this.tasks.length;
    const percent = total > 0 ? Math.round((this.completedCount / total) * 100) : 100;

    this.container.innerHTML = `
      <div class="modal-overlay vyko-modal-overlay">
        <div class="modal-sheet-apple vyko-modal-card" style="max-width: 540px;">
          <!-- iOS Sheet Grab Indicator -->
          <div class="sheet-drag-handle"></div>

          <div class="sheet-header vyko-modal-header">
            <div>
              <h3 class="sheet-title vyko-modal-title">${this.tasks.length === 1 ? 'Acknowledge Handover' : 'Acknowledge All Handovers'}</h3>
              <p class="sheet-subtitle vyko-modal-sub" id="batch-subtitle">
                ${this.isProcessing ? 'Syncing and acknowledging clinical handover notes with hospital server...' : 'Batch process completed. See summary below.'}
              </p>
            </div>
            <div id="modal-close-container">
              ${!this.isProcessing ? `
                <button class="btn-icon vyko-modal-close" id="modal-close-top" title="Close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              ` : ''}
            </div>
          </div>

          <!-- Apple Progress Track -->
          <div style="display: flex; flex-direction: column; gap: 0.45rem;">
            <div style="display: flex; justify-content: space-between; font-size: 0.8rem;">
              <span style="color: var(--text-secondary);" id="progress-text">${this.completedCount} of ${total} processed</span>
              <span style="color: var(--text-primary); font-weight: 600;" id="progress-percent">${percent}%</span>
            </div>
            <div class="ios-progress-track">
              <div class="ios-progress-fill" id="progress-fill" style="width: ${percent}%;"></div>
            </div>
          </div>

          <!-- Summary Badges -->
          <div id="summary-badges" style="display: flex; gap: 0.45rem; flex-wrap: wrap;">
            <span class="apple-pill apple-pill-success" id="badge-success">✓ Acknowledged: ${this.successCount}</span>
            <span class="apple-pill" id="badge-failed" style="display: ${this.failedCount > 0 ? 'inline-flex' : 'none'}; background: var(--system-red-bg); border: 0.5px solid var(--system-red-border); color: var(--system-red);">✕ Failed: ${this.failedCount}</span>
            <span class="apple-pill apple-pill-subtle" id="badge-remaining">Remaining: ${total - this.completedCount}</span>
          </div>

          <!-- Patient Task Inset Group -->
          <div id="tasks-scroll-container" style="max-height: 290px; overflow-y: auto; display: flex; flex-direction: column; gap: 0.4rem; padding-right: 0.2rem;">
            ${this.tasks.map((task) => this.renderTaskRowHTML(task)).join('')}
          </div>

          <!-- Sheet Actions -->
          <div id="sheet-actions" class="vyko-modal-footer" style="display: flex; justify-content: flex-end; gap: 0.6rem; border-top: 0.5px solid var(--border-hairline); padding-top: 0.85rem; margin-top: 0.25rem;">
            ${this.isProcessing ? `
              <button class="btn btn-secondary btn-pill btn-sm" id="btn-abort-batch">
                Stop
              </button>
            ` : `
              <button class="btn btn-primary btn-pill" id="btn-done-batch" style="min-width: 110px;">
                Done
              </button>
            `}
          </div>
        </div>
      </div>
    `;

    this.attachEvents();
  }

  private renderTaskRowHTML(task: HandoverTaskStatus): string {
    let statusIcon = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    `;
    let statusTag = `<span class="apple-pill apple-pill-subtle">Queued</span>`;

    if (task.status === 'in-progress') {
      statusIcon = `<span class="spinner"></span>`;
      statusTag = `<span class="apple-pill apple-pill-subtle" style="color: var(--system-blue);">Syncing...</span>`;
    } else if (task.status === 'success') {
      statusIcon = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--system-green)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      `;
      statusTag = `<span class="apple-pill apple-pill-success">Accepted</span>`;
    } else if (task.status === 'skipped') {
      statusIcon = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      `;
      statusTag = `<span class="apple-pill apple-pill-subtle">Skipped</span>`;
    } else if (task.status === 'failed') {
      statusIcon = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--system-red)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      `;
      statusTag = `<span class="apple-pill" style="background: var(--system-red-bg); border: 0.5px solid var(--system-red-border); color: var(--system-red);">Failed</span>`;
    }

    return `
      <div id="${task.id}" style="display: flex; align-items: center; justify-content: space-between; padding: 0.65rem 0.85rem; background: var(--bg-input); border-radius: var(--radius-sm); border: 0.5px solid var(--border-hairline); font-size: 0.85rem; transition: background 0.15s ease;">
        <div style="display: flex; align-items: center; gap: 0.7rem; min-width: 0;">
          <div class="task-icon-mount" style="display: flex; align-items: center; justify-content: center; width: 18px; height: 18px; flex-shrink: 0;">
            ${statusIcon}
          </div>
          <div style="display: flex; flex-direction: column; min-width: 0;">
            <span style="font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--text-primary); letter-spacing: -0.01em;">
              ${task.patientName}
            </span>
            <span class="task-details-mount" style="font-size: 0.75rem; color: var(--text-secondary);">
              H.No: ${task.hospitalNumber} · IP: ${task.ipNumber}${task.details ? ` · ${task.details}` : ''}
            </span>
          </div>
        </div>
        <div class="task-tag-mount">
          ${statusTag}
        </div>
      </div>
    `;
  }

  // Ultra-fast in-place DOM update without layout reset or scroll jumps
  private updateTaskRowInDOM(task: HandoverTaskStatus) {
    const row = this.container.querySelector(`#${task.id}`);
    if (!row) return;

    const iconMount = row.querySelector('.task-icon-mount');
    const detailsMount = row.querySelector('.task-details-mount');
    const tagMount = row.querySelector('.task-tag-mount');

    if (task.status === 'in-progress') {
      if (iconMount) iconMount.innerHTML = `<span class="spinner"></span>`;
      if (tagMount) tagMount.innerHTML = `<span class="apple-pill apple-pill-subtle" style="color: var(--system-blue);">Syncing...</span>`;
    } else if (task.status === 'success') {
      if (iconMount) {
        iconMount.innerHTML = `
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--system-green)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        `;
      }
      if (tagMount) tagMount.innerHTML = `<span class="apple-pill apple-pill-success">Accepted</span>`;
    } else if (task.status === 'skipped') {
      if (iconMount) {
        iconMount.innerHTML = `
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        `;
      }
      if (tagMount) tagMount.innerHTML = `<span class="apple-pill apple-pill-subtle">Skipped</span>`;
    } else if (task.status === 'failed') {
      if (iconMount) {
        iconMount.innerHTML = `
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--system-red)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        `;
      }
      if (tagMount) tagMount.innerHTML = `<span class="apple-pill" style="background: var(--system-red-bg); border: 0.5px solid var(--system-red-border); color: var(--system-red);">Failed</span>`;
    }

    if (detailsMount) {
      detailsMount.textContent = `H.No: ${task.hospitalNumber} · IP: ${task.ipNumber}${task.details ? ` · ${task.details}` : ''}`;
    }
  }

  // Update progress bar and counters in-place
  private updateProgressInDOM() {
    const total = this.tasks.length;
    const percent = total > 0 ? Math.round((this.completedCount / total) * 100) : 100;

    const textEl = this.container.querySelector('#progress-text');
    if (textEl) textEl.textContent = `${this.completedCount} of ${total} processed`;

    const percentEl = this.container.querySelector('#progress-percent');
    if (percentEl) percentEl.textContent = `${percent}%`;

    const fillEl = this.container.querySelector('#progress-fill') as HTMLElement;
    if (fillEl) fillEl.style.width = `${percent}%`;

    const badgeSuccess = this.container.querySelector('#badge-success');
    if (badgeSuccess) badgeSuccess.textContent = `✓ Acknowledged: ${this.successCount}`;

    const badgeFailed = this.container.querySelector('#badge-failed') as HTMLElement;
    if (badgeFailed) {
      badgeFailed.style.display = this.failedCount > 0 ? 'inline-flex' : 'none';
      badgeFailed.textContent = `✕ Failed: ${this.failedCount}`;
    }

    const badgeRemaining = this.container.querySelector('#badge-remaining');
    if (badgeRemaining) badgeRemaining.textContent = `Remaining: ${total - this.completedCount}`;
  }

  // Update final UI state when all workers complete
  private renderCompletedUI() {
    this.isProcessing = false;
    this.updateProgressInDOM();

    const subtitle = this.container.querySelector('#batch-subtitle');
    if (subtitle) {
      subtitle.textContent = this.shouldAbort
        ? 'Batch process stopped by user. See final status below.'
        : `Batch completed in record time: ${this.successCount} patient(s) acknowledged.`;
    }

    const closeContainer = this.container.querySelector('#modal-close-container');
    if (closeContainer) {
      closeContainer.innerHTML = `
        <button class="btn-icon" id="modal-close-top" title="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      `;
      this.container.querySelector('#modal-close-top')?.addEventListener('click', () => this.close());
    }

    const actionsContainer = this.container.querySelector('#sheet-actions');
    if (actionsContainer) {
      actionsContainer.innerHTML = `
        <button class="btn btn-liquid-accept btn-pill" id="btn-done-batch" style="min-width: 110px;">
          Done
        </button>
      `;
      this.container.querySelector('#btn-done-batch')?.addEventListener('click', () => this.close());
    }
  }

  private attachEvents() {
    this.container.querySelector('#btn-abort-batch')?.addEventListener('click', () => {
      this.shouldAbort = true;
      appLogger.warn('Stop requested by user. Terminating remaining queue...');
    });

    this.container.querySelector('#modal-close-top')?.addEventListener('click', () => {
      this.close();
    });

    this.container.querySelector('#btn-done-batch')?.addEventListener('click', () => {
      this.close();
    });
  }

  private close() {
    this.container.innerHTML = '';
    this.onComplete();
  }

  private scrollToTask(taskId: string) {
    const row = this.container.querySelector(`#${taskId}`) as HTMLElement;
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  // High-speed sequential execution (processing handovers one by one)
  private async startHighSpeedBatchAcceptance() {
    this.isProcessing = true;
    const startTime = performance.now();
    const total = this.tasks.length;
    appLogger.info(`Starting high-speed acceptance for ${total} patient(s) (processing one by one)...`);

    for (let i = 0; i < total; i++) {
      if (this.shouldAbort) {
        appLogger.warn('Stop requested by user. Terminating remaining queue...');
        break;
      }

      const task = this.tasks[i];
      await this.processSinglePatient(task, i + 1, total);
    }

    // If stopped prematurely, mark remaining queued tasks as skipped
    if (this.shouldAbort) {
      for (const task of this.tasks) {
        if (task.status === 'pending') {
          task.status = 'skipped';
          task.details = 'Cancelled by user';
          this.completedCount++;
          this.updateTaskRowInDOM(task);
        }
      }
    }

    const durationSeconds = ((performance.now() - startTime) / 1000).toFixed(1);
    appLogger.success(`Acceptance completed in ${durationSeconds}s! Acknowledged: ${this.successCount}, Failed: ${this.failedCount}`);
    this.renderCompletedUI();
  }

  private async processSinglePatient(task: HandoverTaskStatus, index: number, total: number) {
    task.status = 'in-progress';
    this.updateTaskRowInDOM(task);
    this.scrollToTask(task.id);

    try {
      appLogger.info(`[${index}/${total}] Fetching notes for ${task.patientName} (${task.hospitalNumber})...`);
      const notes = await api.fetchHandover(task.hospitalNumber, task.ipNumber);

      if (!notes || notes.length === 0) {
        task.status = 'skipped';
        task.details = 'No visit notes found';
        this.completedCount++;
        this.updateTaskRowInDOM(task);
        this.updateProgressInDOM();
        return;
      }

      // Find notes assigned to current nurse that are NOT ACKNOWLEDGED
      const pendingNotes = notes.filter((n) => {
        const isTarget = String(n.handoverTo) === String(api.loginId);
        const isNotAck = String(n.ackStatus).toUpperCase() === 'NOT ACKNOWLEDGED' ||
                         String(n.ackStatus).toUpperCase() === 'NTC0001' ||
                         String(n.ackStatus).toUpperCase() === 'PENDING';
        return isTarget && isNotAck;
      });

      if (pendingNotes.length === 0) {
        task.status = 'skipped';
        task.details = 'Already acknowledged or not assigned to me';
        this.completedCount++;
        this.updateTaskRowInDOM(task);
        this.updateProgressInDOM();
        return;
      }

      // Process notes one by one sequentially with rapid execution
      appLogger.info(`[${index}/${total}] Acknowledging ${pendingNotes.length} note(s) for ${task.patientName}...`);
      let noteAcceptedCount = 0;

      for (const note of pendingNotes) {
        if (this.shouldAbort) break;
        try {
          const success = await api.updateHandover(
            note.noteCode,
            task.hospitalNumber,
            task.ipNumber,
            note.reportKey,
            '<p>Accepted</p>'
          );
          if (success) {
            noteAcceptedCount++;
          } else {
            appLogger.warn(`[${index}/${total}] Note ${note.noteCode} rejected by hospital server`);
          }
        } catch (err: any) {
          appLogger.error(`[${index}/${total}] Failed note ${note.noteCode} for ${task.patientName}: ${err?.message}`);
        }
      }

      if (noteAcceptedCount === pendingNotes.length && noteAcceptedCount > 0) {
        task.status = 'success';
        task.details = noteAcceptedCount === 1 ? 'Accepted 1 note' : `Accepted ${noteAcceptedCount} notes`;
        this.successCount++;
        userService.recordAcceptedHandovers(noteAcceptedCount);
        appLogger.success(`[${index}/${total}] ✓ Fully acknowledged: ${task.patientName} (${noteAcceptedCount} note${noteAcceptedCount > 1 ? 's' : ''})`);
      } else if (noteAcceptedCount > 0) {
        task.status = 'success';
        task.details = `Partial: ${noteAcceptedCount}/${pendingNotes.length} accepted`;
        this.successCount++;
        userService.recordAcceptedHandovers(noteAcceptedCount);
        appLogger.warn(`[${index}/${total}] Partial acknowledgement: ${task.patientName} (${noteAcceptedCount}/${pendingNotes.length})`);
      } else {
        task.status = 'failed';
        task.details = 'Server update rejected';
        this.failedCount++;
        appLogger.error(`[${index}/${total}] ✕ Rejected by hospital server for ${task.patientName}`);
      }
    } catch (err: any) {
      task.status = 'failed';
      task.details = err?.message || 'Network error';
      this.failedCount++;
      appLogger.error(`[${index}/${total}] Error processing ${task.patientName}: ${err?.message}`);
    }

    this.completedCount++;
    this.updateTaskRowInDOM(task);
    this.updateProgressInDOM();
  }
}
